/**
 * Persistance temporaire (sessionStorage) du parcours d'ajout / édition de widget.
 * - WidgetDraft : brouillon entre les étapes taille → configurateur
 * - PendingWidget / PendingWidgetUpdate : action à appliquer au retour sur /grid
 */
import { WidgetSizeOption, WidgetTypeId } from "./widgetCatalog";

const DRAFT_KEY = "dashboard-widget-draft";
const PENDING_KEY = "dashboard-pending-widget";

export type WidgetDraft = {
  widgetType: WidgetTypeId;
  sizeId: string;
  config: Record<string, unknown>;
  editingWidgetId?: string;
  /** Dimensions courantes (cellules grille) — modifiables au resize dans le configurateur. */
  w?: number;
  h?: number;
};

export type PendingWidget = {
  widgetType: WidgetTypeId;
  props: Record<string, unknown>;
  w: number;
  h: number;
};

export type PendingWidgetUpdate = PendingWidget & {
  widgetId: string;
};

const UPDATE_KEY = "dashboard-pending-widget-update";

export function setWidgetDraft(draft: WidgetDraft) {
  sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
}

export function getWidgetDraft(): WidgetDraft | null {
  const raw = sessionStorage.getItem(DRAFT_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as WidgetDraft;
  } catch {
    return null;
  }
}

export function clearWidgetDraft() {
  sessionStorage.removeItem(DRAFT_KEY);
}

export function setPendingWidget(widget: PendingWidget) {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(widget));
}

export function consumePendingWidget(): PendingWidget | null {
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) return null;

  sessionStorage.removeItem(PENDING_KEY);

  try {
    return JSON.parse(raw) as PendingWidget;
  } catch {
    return null;
  }
}

export function saveSizeSelection(widgetType: WidgetTypeId, size: WidgetSizeOption) {
  const existing = getWidgetDraft();
  setWidgetDraft({
    widgetType,
    sizeId: size.id,
    config: existing?.widgetType === widgetType ? existing.config : {},
    editingWidgetId: existing?.editingWidgetId,
  });
}

export function setPendingWidgetUpdate(widget: PendingWidgetUpdate) {
  sessionStorage.setItem(UPDATE_KEY, JSON.stringify(widget));
}

export function consumePendingWidgetUpdate(): PendingWidgetUpdate | null {
  const raw = sessionStorage.getItem(UPDATE_KEY);
  if (!raw) return null;

  sessionStorage.removeItem(UPDATE_KEY);

  try {
    return JSON.parse(raw) as PendingWidgetUpdate;
  } catch {
    return null;
  }
}

export function parseWidgetContent(content?: string) {
  if (!content) return null;

  try {
    return JSON.parse(content) as { name: string; props: Record<string, unknown> };
  } catch {
    return null;
  }
}
